class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.searchContent = this.querySelector('.search__content');
    
    // Check if the pre-search recommendations are empty
    this.noDefaultPreSearchContent = this.dataset.searchRecommendationEmpty === 'true';

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this.input) {
      if (this.input.form) {
        this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      }
      this.input.addEventListener('input', this.debounce(this.onChange.bind(this), 300));
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }
    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.clear.bind(this));
    }
    
    // Close dropdown on click outside
    document.addEventListener('click', this.handleClickOutside.bind(this));
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  clear(event = null) {
    if (event) event.preventDefault();
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    if (this.noDefaultPreSearchContent && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.removeAttribute('results');
    this.removeAttribute('loading');
  }

  getSectionId() {
    const sectionElement = this.closest('.shopify-section') || this.closest('[data-section-id]');
    if (sectionElement) {
      if (sectionElement.hasAttribute('data-section-id')) {
        return sectionElement.getAttribute('data-section-id');
      }
      return sectionElement.id.replace('shopify-section-', '');
    }
    return null;
  }

  setupURL() {
    const limit = this.dataset.resultsLimit || 4;
    const sectionId = this.getSectionId() || 'predictive-search';
    
    const rootUrl = window.Shopify?.routes?.root || '/';
    const baseUrl = `${rootUrl}search/suggest`.replace(/\/+/g, '/');
    const url = new URL(window.location.origin + baseUrl);
    
    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('resources[limit]', limit);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', sectionId);
    return url;
  }

  onFocus(event) {
    document.body.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      // If there are recommendations, show them when focused
      if (this.searchContent && !this.noDefaultPreSearchContent) {
        this.searchContent.classList.remove('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  renderSection(url) {
    if (this.cachedResults[url]) {
      this.renderSearchResults(this.cachedResults[url]);
    } else {
      this.renderSectionFromFetch(url);
    }
  }

  renderSectionFromFetch(url) {
    this.setAttribute('loading', 'true');
    if (this.resetButton) {
      this.resetButton.classList.add('btn--loading');
    }

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedResults[url] = responseText;
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        this.removeAttribute('loading');
        if (this.resetButton) {
          this.resetButton.classList.remove('btn--loading');
        }
        this.setAttribute('results', 'true');
      });
  }

  renderSearchResults(responseText) {
    const sectionId = this.getSectionId();
    if (!sectionId) {
      console.error('Predictive search: missing section id on form.');
      return;
    }
    const resultsId = 'PredictiveSearchResults-' + sectionId;
    let targetElement = this.querySelector('[id="' + resultsId.replace(/"/g, '\\"') + '"]');
    if (!targetElement) {
      targetElement = this.querySelector('#predictive-search-results') || this.querySelector('[data-predictive-search]');
    }

    if (!targetElement) {
      console.error(`Element with id '${resultsId}' or default results container not found inside this search form.`);
      return;
    }

    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(responseText, 'text/html');
    let contentElement = parsedDoc.getElementById(resultsId);
    if (!contentElement) {
      const sectionRoot = parsedDoc.getElementById('shopify-section-' + sectionId);
      contentElement = sectionRoot?.querySelector('[id="' + resultsId.replace(/"/g, '\\"') + '"]') ?? null;
    }
    if (!contentElement) {
      contentElement = parsedDoc.getElementById('predictive-search-results') || parsedDoc.querySelector('[data-predictive-search]');
    }

    if (contentElement) {
      if (this.searchContent) {
        this.searchContent.classList.remove('hidden');
      }
      targetElement.innerHTML = contentElement.innerHTML;
    } else {
      console.error(`Element with id '${resultsId}' or default results container not found in the parsed suggest response.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      document.body.classList.remove('predictive-search-open');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
